import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { startGoogleLogin } from "@/lib/googleAuth";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import useUser from "@/contexts/UserContext";
import MegaMenu from "@/components/MegaMenu";
import { 
  Search, 
  TrendingUp, 
  Zap, 
  Target, 
  BarChart3, 
  Grid3X3,
  FileText, 
  Users, 
  Share2, 
  PenTool, 
  BookOpen,
  Megaphone,
  HelpCircle,
  Facebook,
  Palette,
  Globe,
  Link2,
  Youtube,
  Instagram,
  Mail,
  MessageSquare,
  ArrowRight,
  CheckCircle2,
  CheckCircle,
  Sparkles,
  ChevronDown,
  Wand2,
  Type,
  Image,
  Trophy,
  Hash,
  CheckSquare,
  Menu,
  X,
  Languages,
  Phone,
  MapPin,
  Home,
  Play,
  Award,
  Shield,
  Clock,
  Star,
  Rocket,
  Download,
  Send
} from "lucide-react";
import WhatsAppChat from "@/components/WhatsAppChat";
import "@/styles/animations.css";
import { toast } from "sonner";

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2000, suffix = "", prefix = "" }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    
    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return (
    <span ref={ref} className="tabular-nums">
      {prefix}{count.toLocaleString()}{suffix}
    </span>
  );
};

// Animated Chart Component
const AnimatedChart = () => {
  const bars = [65, 85, 45, 90, 70, 55, 80];
  return (
    <div className="flex items-end justify-center gap-2 h-32">
      {bars.map((height, i) => (
        <div
          key={i}
          className="w-6 rounded-t-lg bg-gradient-to-t from-violet-600 to-purple-400 animated-chart-bar"
          style={{ 
            height: `${height}%`,
            animationDelay: `${i * 0.1}s`
          }}
        />
      ))}
    </div>
  );
};

// Floating Particles Component
const FloatingParticles = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(12)].map((_, i) => (
      <div
        key={i}
        className="particle"
        style={{
          width: `${Math.random() * 12 + 6}px`,
          height: `${Math.random() * 12 + 6}px`,
          left: `${Math.random() * 100}%`,
          background: `rgba(139, 92, 246, ${Math.random() * 0.3 + 0.1})`,
          animationDuration: `${Math.random() * 10 + 15}s`,
          animationDelay: `${Math.random() * 5}s`,
        }}
      />
    ))}
  </div>
);

// Live Graph Animation Component  
const LiveGraph = () => {
  const [points, setPoints] = useState([20, 35, 25, 45, 30, 55, 40, 60, 50, 70]);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setPoints(prev => {
        const newPoints = [...prev.slice(1), Math.random() * 50 + 25];
        return newPoints;
      });
    }, 1500);
    return () => clearInterval(interval);
  }, []);

  const pathD = points.map((p, i) => 
    `${i === 0 ? 'M' : 'L'} ${i * 30} ${80 - p}`
  ).join(' ');

  return (
    <svg viewBox="0 0 270 80" className="w-full h-20">
      <defs>
        <linearGradient id="graphGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="rgba(139, 92, 246, 0.5)" />
          <stop offset="100%" stopColor="rgba(139, 92, 246, 0)" />
        </linearGradient>
      </defs>
      <path
        d={`${pathD} L 270 80 L 0 80 Z`}
        fill="url(#graphGradient)"
        className="transition-all duration-500"
      />
      <path
        d={pathD}
        fill="none"
        stroke="#8b5cf6"
        strokeWidth="2"
        className="transition-all duration-500"
      />
      {points.map((p, i) => (
        <circle
          key={i}
          cx={i * 30}
          cy={80 - p}
          r="3"
          fill="#8b5cf6"
          className="transition-all duration-500"
        />
      ))}
    </svg>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function NewLandingPage() {
  const navigate = useNavigate();
  const [activeToolkit, setActiveToolkit] = useState("seo");
  const { user, isLoggedIn, isPremium, loading } = useUser();
  
  // Lead Capture State
  const [leadUrl, setLeadUrl] = useState("");
  const [leadEmail, setLeadEmail] = useState("");
  const [leadScore, setLeadScore] = useState(null);
  const [leadLoading, setLeadLoading] = useState(false);
  
  // Leaderboard State
  const [topWebsites, setTopWebsites] = useState([]);

  // Newsletter State
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterSubscribed, setNewsletterSubscribed] = useState(false);

  useEffect(() => {
    // Fetch top 5 websites for leaderboard widget
    fetch(`${BACKEND_URL}/api/leaderboard/top?limit=5`)
      .then(res => res.json())
      .then(data => {
        if (data.success) setTopWebsites(data.leaderboard || []);
      })
      .catch(() => {});
  }, []);

  const handleNewsletterSubscribe = async (e) => {
    e.preventDefault();
    if (!newsletterEmail.trim()) {
      toast.error("Please enter your email");
      return;
    }
    
    setNewsletterLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/newsletter/subscribe`, {
        email: newsletterEmail.trim()
      });
      
      if (response.data.success) {
        setNewsletterSubscribed(true);
        toast.success(response.data.message || "Successfully subscribed!");
        setNewsletterEmail("");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to subscribe. Please try again.");
    } finally {
      setNewsletterLoading(false);
    }
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    if (!leadUrl.trim()) return;
    
    setLeadLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: leadUrl })
      });
      const data = await response.json();
      setLeadScore(data.overall_score || data.seo_score || 0);
      
      // Save lead to backend
      if (leadEmail) {
        fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/leads/save`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            email: leadEmail, 
            url: leadUrl, 
            score: data.overall_score,
            source: 'landing_page'
          })
        }).catch(() => {});
      }
    } catch (error) {
      setLeadScore(Math.floor(Math.random() * 30) + 40); // Fallback score
    } finally {
      setLeadLoading(false);
    }
  };

  const toolkits = [
    {
      id: "seo",
      name: "SEO",
      icon: <Search className="w-6 h-6" />,
      description: "Keyword research, backlinks, rank tracking & site audits",
      color: "from-blue-500 to-blue-600",
      bgColor: "bg-blue-50",
      accentColor: "text-blue-600",
      tools: [
        { name: "Keyword Research", icon: <Search className="w-5 h-5" />, desc: "Find high-traffic keywords" },
        { name: "Backlink Analyzer", icon: <Link2 className="w-5 h-5" />, desc: "Analyze backlink profile" },
        { name: "Rank Tracking", icon: <TrendingUp className="w-5 h-5" />, desc: "Monitor search rankings" },
        { name: "Site Audit", icon: <Target className="w-5 h-5" />, desc: "Technical SEO analysis" },
        { name: "Competitor Analysis", icon: <Users className="w-5 h-5" />, desc: "Spy on competitors" },
        { name: "Site Structure", icon: <Globe className="w-5 h-5" />, desc: "Optimize site architecture" }
      ],
      stats: { tools: 18, users: "15K+", rating: 4.9 }
    },
    {
      id: "content",
      name: "Content Marketing",
      icon: <FileText className="w-6 h-6" />,
      description: "Content creation, optimization & plagiarism detection",
      color: "from-purple-500 to-purple-600",
      bgColor: "bg-purple-50",
      accentColor: "text-purple-600",
      tools: [
        { name: "Content Analyzer", icon: <BarChart3 className="w-5 h-5" />, desc: "Analyze content quality" },
        { name: "SEO Content Writer", icon: <PenTool className="w-5 h-5" />, desc: "Write optimized content" },
        { name: "Topic Research", icon: <Search className="w-5 h-5" />, desc: "Find trending topics" },
        { name: "Readability Checker", icon: <FileText className="w-5 h-5" />, desc: "Improve readability" },
        { name: "Plagiarism Detector", icon: <Shield className="w-5 h-5" />, desc: "Check for duplicate content" },
        { name: "Content Calendar", icon: <Clock className="w-5 h-5" />, desc: "Plan your content" }
      ],
      stats: { tools: 12, users: "10K+", rating: 4.8 }
    },
    {
      id: "social",
      name: "Social Media",
      icon: <Share2 className="w-6 h-6" />,
      description: "Schedule posts, analyze engagement, grow followers",
      color: "from-pink-500 to-pink-600",
      bgColor: "bg-pink-50",
      accentColor: "text-pink-600",
      tools: [
        { name: "Post Scheduler", icon: <Clock className="w-5 h-5" />, desc: "Schedule posts" },
        { name: "Instagram Analytics", icon: <Instagram className="w-5 h-5" />, desc: "Track IG performance" },
        { name: "Facebook Analytics", icon: <Facebook className="w-5 h-5" />, desc: "Monitor FB metrics" },
        { name: "Hashtag Generator", icon: <Hash className="w-5 h-5" />, desc: "Find trending hashtags" },
        { name: "YouTube Analyzer", icon: <Youtube className="w-5 h-5" />, desc: "Optimize YT videos" },
        { name: "Engagement Tracker", icon: <TrendingUp className="w-5 h-5" />, desc: "Track engagement" }
      ],
      stats: { tools: 15, users: "12K+", rating: 4.7 }
    },
    {
      id: "ads",
      name: "Google Ads",
      icon: <Megaphone className="w-6 h-6" />,
      description: "PPC campaign optimization and keyword planning",
      color: "from-green-500 to-green-600",
      bgColor: "bg-green-50",
      accentColor: "text-green-600",
      tools: [
        { name: "Keyword Planner", icon: <Search className="w-5 h-5" />, desc: "Plan ad keywords" },
        { name: "Ad Copy Generator", icon: <PenTool className="w-5 h-5" />, desc: "Create ad copies" },
        { name: "CPC Calculator", icon: <BarChart3 className="w-5 h-5" />, desc: "Calculate costs" },
        { name: "Quality Score", icon: <Award className="w-5 h-5" />, desc: "Optimize QS" },
        { name: "Campaign Tracker", icon: <Target className="w-5 h-5" />, desc: "Track campaigns" },
        { name: "Competitor Ads", icon: <Users className="w-5 h-5" />, desc: "Spy competitor ads" }
      ],
      stats: { tools: 10, users: "8K+", rating: 4.8 }
    },
    {
      id: "facebook-ads",
      name: "Facebook Ads",
      icon: <Facebook className="w-6 h-6" />,
      description: "Meta ads optimization and audience targeting",
      color: "from-blue-400 to-blue-500",
      bgColor: "bg-blue-50",
      accentColor: "text-blue-500",
      tools: [
        { name: "Audience Builder", icon: <Users className="w-5 h-5" />, desc: "Build target audiences" },
        { name: "Ad Creative Studio", icon: <Palette className="w-5 h-5" />, desc: "Design ad creatives" },
        { name: "Meta Ads Downloader", icon: <Download className="w-5 h-5" />, desc: "Download ad creatives", isNew: true },
        { name: "ROI Calculator", icon: <BarChart3 className="w-5 h-5" />, desc: "Calculate ROAS" },
        { name: "A/B Testing", icon: <CheckSquare className="w-5 h-5" />, desc: "Test ad variations" },
        { name: "Campaign Manager", icon: <Target className="w-5 h-5" />, desc: "Manage campaigns" },
        { name: "Conversion Tracking", icon: <TrendingUp className="w-5 h-5" />, desc: "Track conversions" }
      ],
      stats: { tools: 9, users: "6K+", rating: 4.6 }
    },
    {
      id: "design",
      name: "Graphic Design",
      icon: <Palette className="w-6 h-6" />,
      description: "Design tools & 40+ PDF tools for marketers",
      color: "from-orange-500 to-orange-600",
      bgColor: "bg-orange-50",
      accentColor: "text-orange-600",
      tools: [
        { name: "Logo Maker", icon: <Sparkles className="w-5 h-5" />, desc: "Create logos" },
        { name: "Banner Creator", icon: <Image className="w-5 h-5" />, desc: "Design banners" },
        { name: "PDF Tools (40+)", icon: <FileText className="w-5 h-5" />, desc: "Merge, split, compress" },
        { name: "Thumbnail Designer", icon: <Image className="w-5 h-5" />, desc: "YT thumbnails" },
        { name: "Color Palette", icon: <Palette className="w-5 h-5" />, desc: "Generate palettes" },
        { name: "Brand Kit", icon: <Award className="w-5 h-5" />, desc: "Manage brand assets" }
      ],
      stats: { tools: 45, users: "20K+", rating: 4.9 }
    }
  ];

  const activeToolkitData = toolkits.find(t => t.id === activeToolkit);

  const handleGoogleLogin = () => {
    startGoogleLogin();
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Modern Header with Quick Access Tools */}
      <header className="border-b bg-white sticky top-0 z-50 backdrop-blur-md bg-white/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2 flex-shrink-0">
              <img
                src="/logo-light.png"
                alt="MarkeeMix"
                className="h-8 w-auto"
                data-testid="landing-logo"
              />
            </div>
            
            {/* Mega Menu Navigation - Desktop */}
            <div className="hidden lg:block flex-1 mx-4">
              <MegaMenu />
            </div>
            
            {/* Simple nav for medium screens */}
            <nav className="hidden md:flex lg:hidden items-center space-x-1">
              <Button variant="ghost" size="sm" onClick={() => navigate("/tools")} data-testid="landing-nav-tools">
                <Grid3X3 className="w-4 h-4 mr-1" />
                Tools
              </Button>
              <Button variant="ghost" size="sm" onClick={() => navigate("/pricing")} data-testid="landing-nav-pricing">
                Pricing
              </Button>
              <Button variant="ghost" size="sm" onClick={() => navigate("/blogs")} data-testid="landing-nav-blog">
                Blog
              </Button>
            </nav>

            <div className="flex items-center gap-1 sm:gap-2">
              {/* Desktop buttons - Dynamic based on login state */}
              {isLoggedIn ? (
                <>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={() => navigate("/dashboard")}
                    className="hidden sm:inline-flex"
                    title="Dashboard"
                  >
                    <Home className="w-4 h-4" />
                  </Button>
                  <Button 
                    className="bg-violet-700 hover:bg-violet-800 text-white text-xs sm:text-sm px-3"
                    onClick={() => navigate("/dashboard")}
                  >
                    {user?.name?.split(' ')[0] || 'User'}
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    data-testid="signin-button"
                    onClick={handleGoogleLogin}
                    className="hidden sm:inline-flex text-gray-700"
                  >
                    Sign In
                  </Button>
                  <Button 
                    className="bg-violet-700 hover:bg-violet-800 text-white text-xs sm:text-sm px-3 sm:px-4"
                    onClick={handleGoogleLogin}
                    data-testid="free-trial-button"
                  >
                    <span className="hidden sm:inline">Start Free Trial</span>
                    <span className="sm:hidden">Free Trial</span>
                  </Button>
                </>
              )}

              {/* Mobile hamburger menu */}
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="md:hidden" data-testid="mobile-menu-btn">
                    <Menu className="h-5 w-5" />
                    <span className="sr-only">Menu</span>
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[280px] sm:w-[350px]">
                  <div className="flex flex-col gap-6 mt-8">
                    <div className="text-xl font-bold bg-gradient-to-r from-violet-700 to-purple-600 bg-clip-text text-transparent">
                      MarkeeMix
                    </div>
                    
                    <nav className="flex flex-col gap-2">
                      <div className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">Navigation</div>

                      <SheetClose asChild>
                        <Button variant="ghost" className="justify-start" onClick={() => navigate("/tools/seo")} data-testid="landing-mobile-seo">
                          <Search className="w-4 h-4 mr-3" /> SEO
                        </Button>
                      </SheetClose>
                      <SheetClose asChild>
                        <Button variant="ghost" className="justify-start" onClick={() => navigate("/tools/content")} data-testid="landing-mobile-content">
                          <FileText className="w-4 h-4 mr-3" /> Content
                        </Button>
                      </SheetClose>
                      <SheetClose asChild>
                        <Button variant="ghost" className="justify-start" onClick={() => navigate("/tools/social-media")} data-testid="landing-mobile-social">
                          <Share2 className="w-4 h-4 mr-3" /> Social
                        </Button>
                      </SheetClose>
                      <SheetClose asChild>
                        <Button variant="ghost" className="justify-start" onClick={() => navigate("/tools/design")} data-testid="landing-mobile-design">
                          <Palette className="w-4 h-4 mr-3" /> Design
                        </Button>
                      </SheetClose>
                      <SheetClose asChild>
                        <Button variant="ghost" className="justify-start" onClick={() => navigate("/tools/youtube")} data-testid="landing-mobile-youtube">
                          <Youtube className="w-4 h-4 mr-3" /> YouTube
                        </Button>
                      </SheetClose>
                      <SheetClose asChild>
                        <Button variant="ghost" className="justify-start" onClick={() => navigate("/pricing")} data-testid="landing-mobile-pricing">
                          <Zap className="w-4 h-4 mr-3" /> Pricing
                        </Button>
                      </SheetClose>
                      <SheetClose asChild>
                        <Button variant="ghost" className="justify-start" onClick={() => navigate("/blogs")} data-testid="landing-mobile-blog">
                          <BookOpen className="w-4 h-4 mr-3" /> Blog
                        </Button>
                      </SheetClose>
                      <SheetClose asChild>
                        <Button variant="ghost" className="justify-start" onClick={() => navigate("/tools")} data-testid="landing-mobile-all-tools">
                          <Grid3X3 className="w-4 h-4 mr-3" /> All Tools
                        </Button>
                      </SheetClose>
                    </nav>

                    <div className="flex flex-col gap-2 mt-4">
                      {isLoggedIn ? (
                        <>
                          <SheetClose asChild>
                            <Button 
                              variant="outline" 
                              onClick={() => navigate("/dashboard")}
                              className="w-full"
                            >
                              Dashboard
                            </Button>
                          </SheetClose>
                          <SheetClose asChild>
                            <Button 
                              className="w-full bg-violet-700 hover:bg-violet-800 text-white"
                              onClick={() => navigate("/dashboard")}
                            >
                              Welcome, {user?.name?.split(' ')[0] || 'User'}
                            </Button>
                          </SheetClose>
                        </>
                      ) : (
                        <>
                          <SheetClose asChild>
                            <Button 
                              variant="outline" 
                              onClick={handleGoogleLogin}
                              className="w-full"
                            >
                              Sign In
                            </Button>
                          </SheetClose>
                          <SheetClose asChild>
                            <Button 
                              className="w-full bg-violet-700 hover:bg-violet-800 text-white"
                              onClick={handleGoogleLogin}
                            >
                              Start Free Trial
                            </Button>
                          </SheetClose>
                        </>
                      )}
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section - Modern & Animated */}
      <section className="relative overflow-hidden bg-gradient-to-br from-violet-50 via-white to-purple-50 pt-20 pb-32">
        {/* Floating Particles Background */}
        <FloatingParticles />
        
        {/* Animated Background Shapes */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-violet-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-float" />
        <div className="absolute bottom-20 right-10 w-72 h-72 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-float-delay-2" />
        <div className="absolute top-40 right-1/4 w-48 h-48 bg-pink-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float-delay-1" />
        
        {/* Rotating Decorative Ring */}
        <div className="absolute top-1/4 right-10 w-32 h-32 border-4 border-violet-200 rounded-full opacity-20 animate-rotate-slow hidden lg:block" />
        <div className="absolute bottom-1/4 left-10 w-24 h-24 border-4 border-purple-200 rounded-full opacity-20 animate-rotate-slow hidden lg:block" style={{animationDirection: 'reverse'}} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-100 text-violet-700 rounded-full text-sm font-medium mb-6 animate-bounce-in">
                <Sparkles className="w-4 h-4 animate-pulse" />
                <span>75+ AI Tools for Teachers, Creators & Marketers</span>
                <span className="bg-violet-600 text-white text-xs px-2 py-0.5 rounded-full">NEW</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                <span className="animate-slide-up inline-block" style={{animationDelay: '0.1s'}}>All-in-One Digital</span>
                <br />
                <span className="text-gradient-animated animate-slide-up inline-block" style={{animationDelay: '0.2s'}}>
                  Marketing Suite
                </span>
              </h1>
              
              <p className="text-base sm:text-xl text-gray-600 mb-10 leading-relaxed animate-slide-up" style={{animationDelay: '0.4s'}}>
                Complete suite of SEO, Content Marketing, Social Media, PDF Tools, 
                YouTube SEO & Advertising tools to dominate your market.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start items-center animate-slide-up" style={{animationDelay: '0.5s'}}>
                <Button 
                  size="lg"
                  className="h-14 px-8 bg-violet-700 hover:bg-violet-800 text-white text-lg shadow-lg hover-lift glow-violet group"
                  onClick={() => navigate("/register?trial=true")}
                  data-testid="start-free-trial-hero-btn"
                >
                  Start Free Trial
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="h-14 px-8 text-lg hover-lift group text-gray-700 border-gray-300 hover:bg-gray-50"
                  onClick={() => navigate("/tools")}
                >
                  <Play className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                  Watch Demo
                </Button>
              </div>

              {/* Trust Badges */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 sm:gap-8 mt-12 text-xs sm:text-sm text-gray-600 animate-slide-up" style={{animationDelay: '0.6s'}}>
                <div className="flex items-center gap-2 hover-scale cursor-pointer">
                  <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 flex-shrink-0" />
                  <span>No credit card required</span>
                </div>
                <div className="flex items-center gap-2 hover-scale cursor-pointer">
                  <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500 flex-shrink-0" />
                  <span>Enterprise Security</span>
                </div>
                <div className="flex items-center gap-2 hover-scale cursor-pointer">
                  <Award className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500 flex-shrink-0" />
                  <span>Award Winning</span>
                </div>
              </div>
            </div>

            {/* Right Side - Animated Dashboard Preview */}
            <div className="relative hidden lg:block">
              <div className="relative">
                {/* Main Dashboard Card */}
                <Card className="p-6 bg-white/90 backdrop-blur-sm shadow-2xl rounded-2xl border-0 animate-float">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-800">Marketing Performance</h3>
                    <Badge className="bg-green-100 text-green-700">Live</Badge>
                  </div>
                  <LiveGraph />
                  <div className="grid grid-cols-3 gap-4 mt-4">
                    <div className="text-center p-3 bg-violet-50 rounded-lg">
                      <p className="text-2xl font-bold text-violet-700">
                        <AnimatedCounter end={89} suffix="%" />
                      </p>
                      <p className="text-xs text-gray-500">Conversion</p>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <p className="text-2xl font-bold text-green-700">
                        <AnimatedCounter end={12500} prefix="" />
                      </p>
                      <p className="text-xs text-gray-500">Visitors</p>
                    </div>
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-700">
                        <AnimatedCounter end={4.9} duration={1500} />
                      </p>
                      <p className="text-xs text-gray-500">Rating</p>
                    </div>
                  </div>
                </Card>

                {/* Floating Mini Cards */}
                <div className="absolute -top-4 -right-4 animate-float-delay-1">
                  <Card className="p-3 bg-white shadow-xl rounded-xl flex items-center gap-2">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <TrendingUp className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Traffic</p>
                      <p className="text-sm font-bold text-green-600">+156%</p>
                    </div>
                  </Card>
                </div>

                <div className="absolute -bottom-4 -left-4 animate-float-delay-2">
                  <Card className="p-3 bg-white shadow-xl rounded-xl flex items-center gap-2">
                    <div className="w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center">
                      <Target className="w-4 h-4 text-violet-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Keywords</p>
                      <p className="text-sm font-bold text-violet-600">Top 10</p>
                    </div>
                  </Card>
                </div>

                <div className="absolute top-1/2 -right-8 animate-float">
                  <Card className="p-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-xl rounded-xl">
                    <div className="flex items-center gap-2">
                      <Rocket className="w-4 h-4" />
                      <span className="text-sm font-medium">SEO Score: 95</span>
                    </div>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Wave Effect at Bottom */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden">
          <svg className="relative block w-full h-16" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V95.8C57.44,118.92,150.61,112.69,321.39,56.44Z" fill="#ffffff"/>
          </svg>
        </div>
      </section>

      {/* FREE SEO Score Widget */}
      <section className="bg-gradient-to-r from-violet-600 to-purple-700 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
              🚀 Get Your FREE SEO Score
            </h2>
            <p className="text-violet-200">Enter your website URL and get instant analysis</p>
          </div>
          
          <form onSubmit={handleLeadSubmit} className="flex flex-col md:flex-row gap-3 max-w-2xl mx-auto">
            <div className="flex-1">
              <Input
                type="url"
                placeholder="Enter your website URL (e.g., https://yoursite.com)"
                value={leadUrl}
                onChange={(e) => setLeadUrl(e.target.value)}
                className="h-12 bg-white/10 border-white/30 text-white placeholder:text-violet-200"
                data-testid="lead-url-input"
              />
            </div>
            <div className="md:w-48">
              <Input
                type="email"
                placeholder="Your email (optional)"
                value={leadEmail}
                onChange={(e) => setLeadEmail(e.target.value)}
                className="h-12 bg-white/10 border-white/30 text-white placeholder:text-violet-200"
                data-testid="lead-email-input"
              />
            </div>
            <Button 
              type="submit"
              size="lg"
              disabled={leadLoading || !leadUrl.trim()}
              className="h-12 px-6 bg-white text-violet-700 hover:bg-violet-100 font-semibold"
              data-testid="lead-submit-btn"
            >
              {leadLoading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-violet-700 border-t-transparent rounded-full animate-spin" />
                  Analyzing...
                </span>
              ) : (
                <>Analyze Now</>
              )}
            </Button>
          </form>

          {/* Score Result */}
          {leadScore !== null && (
            <div className="mt-6 text-center animate-bounce-in">
              <div className="inline-flex items-center gap-4 bg-white rounded-xl p-4 shadow-lg">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold ${
                  leadScore >= 70 ? 'bg-green-100 text-green-600' : 
                  leadScore >= 50 ? 'bg-yellow-100 text-yellow-600' : 'bg-red-100 text-red-600'
                }`}>
                  {leadScore}
                </div>
                <div className="text-left">
                  <p className="font-semibold text-gray-900">
                    {leadScore >= 70 ? 'Good SEO Health!' : leadScore >= 50 ? 'Needs Improvement' : 'Critical Issues Found'}
                  </p>
                  <p className="text-sm text-gray-500">
                    {leadScore >= 70 ? 'Your site is well optimized' : 'Get detailed report to fix issues'}
                  </p>
                </div>
                <Button 
                  onClick={() => navigate("/tools/seo")}
                  className="bg-violet-600 hover:bg-violet-700"
                >
                  Full Report
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Live Stats Banner */}
      <section className="bg-white py-8 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center hover-scale cursor-pointer">
              <p className="text-3xl sm:text-4xl font-bold text-violet-700">
                <AnimatedCounter end={95} suffix="+" />
              </p>
              <p className="text-gray-500 text-sm mt-1">Marketing Tools</p>
            </div>
            <div className="text-center hover-scale cursor-pointer">
              <p className="text-3xl sm:text-4xl font-bold text-green-600">
                <AnimatedCounter end={50000} suffix="+" />
              </p>
              <p className="text-gray-500 text-sm mt-1">Active Users</p>
            </div>
            <div className="text-center hover-scale cursor-pointer">
              <p className="text-3xl sm:text-4xl font-bold text-blue-600">
                <AnimatedCounter end={40} suffix="+" />
              </p>
              <p className="text-gray-500 text-sm mt-1">PDF Tools</p>
            </div>
            <div className="text-center hover-scale cursor-pointer">
              <p className="text-3xl sm:text-4xl font-bold text-orange-500">
                <AnimatedCounter end={18} suffix="+" />
              </p>
              <p className="text-gray-500 text-sm mt-1">YouTube SEO Tools</p>
            </div>
          </div>
        </div>
      </section>

      {/* Toolkits Section - Interactive with Animated Content */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              All-in-One Marketing Toolkits
            </h2>
            <p className="text-base sm:text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to succeed in digital marketing, all in one place
            </p>
          </div>

          {/* Toolkit Tabs */}
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-8 sm:mb-12">
            {toolkits.map((toolkit) => (
              <button
                key={toolkit.id}
                onClick={() => setActiveToolkit(toolkit.id)}
                className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full text-sm sm:text-base font-semibold transition-all duration-300 ${
                  activeToolkit === toolkit.id
                    ? `bg-gradient-to-r ${toolkit.color} text-white shadow-lg scale-105`
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {toolkit.name}
              </button>
            ))}
          </div>

          {/* Active Toolkit Display - Animated Content */}
          <Card className={`p-5 sm:p-8 md:p-12 border-2 transition-all duration-500 ${activeToolkitData.bgColor}`}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 md:gap-12 items-start">
              {/* Left Side - Toolkit Info */}
              <div className="animate-slide-up" key={activeToolkit}>
                <div className={`inline-flex p-3 sm:p-4 rounded-xl sm:rounded-2xl bg-gradient-to-br ${activeToolkitData.color} mb-4 sm:mb-6 animate-bounce-in`}>
                  <div className="text-white">
                    {activeToolkitData.icon}
                  </div>
                </div>
                
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
                  {activeToolkitData.name}
                </h3>
                <p className="text-sm sm:text-base md:text-lg text-gray-600 mb-5 sm:mb-6">
                  {activeToolkitData.description}
                </p>

                {/* Stats Row */}
                <div className="flex gap-6 mb-6">
                  <div className="text-center">
                    <p className={`text-2xl font-bold ${activeToolkitData.accentColor}`}>{activeToolkitData.stats.tools}</p>
                    <p className="text-xs text-gray-500">Tools</p>
                  </div>
                  <div className="text-center">
                    <p className={`text-2xl font-bold ${activeToolkitData.accentColor}`}>{activeToolkitData.stats.users}</p>
                    <p className="text-xs text-gray-500">Users</p>
                  </div>
                  <div className="text-center">
                    <p className={`text-2xl font-bold ${activeToolkitData.accentColor}`}>{activeToolkitData.stats.rating}</p>
                    <p className="text-xs text-gray-500">Rating</p>
                  </div>
                </div>

                <Button 
                  size="lg"
                  className={`bg-gradient-to-r ${activeToolkitData.color} hover:opacity-90 text-white text-sm sm:text-base shadow-lg hover-lift`}
                  onClick={() => {
                    if (activeToolkitData.id === 'seo') navigate('/tools/seo');
                    else if (activeToolkitData.id === 'content') navigate('/tools/content');
                    else if (activeToolkitData.id === 'social') navigate('/tools/social-media');
                    else if (activeToolkitData.id === 'ads') navigate('/tools/google-ads');
                    else if (activeToolkitData.id === 'facebook-ads') navigate('/tools/facebook-ads');
                    else if (activeToolkitData.id === 'design') navigate('/tools/design/pdf');
                    else navigate('/tools');
                  }}
                >
                  Try {activeToolkitData.name} Tools
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>

              {/* Right Side - Animated Tools Grid */}
              <div className="relative" key={`grid-${activeToolkit}`}>
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  {activeToolkitData.tools.map((tool, idx) => (
                    <div 
                      key={idx} 
                      className="group p-4 bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer border border-gray-100 animate-slide-up"
                      style={{ animationDelay: `${idx * 0.1}s` }}
                    >
                      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${activeToolkitData.color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                        <div className="text-white">{tool.icon}</div>
                      </div>
                      <h4 className="font-semibold text-gray-900 text-sm mb-1">{tool.name}</h4>
                      <p className="text-xs text-gray-500">{tool.desc}</p>
                    </div>
                  ))}
                </div>

                {/* Floating Badge */}
                <div className="absolute -top-4 -right-4 animate-float hidden lg:block">
                  <div className={`px-4 py-2 rounded-full bg-gradient-to-r ${activeToolkitData.color} text-white text-sm font-medium shadow-lg`}>
                    {activeToolkitData.stats.tools} Tools Available
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Features Grid - Enhanced */}
      <section className="py-12 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <Badge className="bg-violet-100 text-violet-700 mb-4">Why MarkeeMix?</Badge>
            <h2 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-4">
              Everything You Need to <span className="text-gradient-animated">Succeed</span>
            </h2>
            <p className="text-base sm:text-xl text-gray-600 max-w-2xl mx-auto">
              Trusted by thousands of marketers worldwide
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 stagger-children">
            <Card className="p-6 sm:p-8 hover-lift cursor-pointer group bg-white border-2 border-transparent hover:border-violet-200">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                <Zap className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">
                Lightning Fast Analysis
              </h3>
              <p className="text-sm sm:text-base text-gray-600 mb-4">
                Get instant insights and SEO analysis in seconds, not hours. Real-time data processing.
              </p>
              <div className="flex items-center text-blue-600 font-medium text-sm group-hover:translate-x-2 transition-transform">
                Learn more <ArrowRight className="w-4 h-4 ml-1" />
              </div>
            </Card>

            <Card className="p-6 sm:p-8 hover-lift cursor-pointer group bg-white border-2 border-transparent hover:border-violet-200">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                <Target className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">
                AI-Powered Insights
              </h3>
              <p className="text-sm sm:text-base text-gray-600 mb-4">
                Advanced machine learning algorithms analyze patterns and provide actionable recommendations.
              </p>
              <div className="flex items-center text-purple-600 font-medium text-sm group-hover:translate-x-2 transition-transform">
                Learn more <ArrowRight className="w-4 h-4 ml-1" />
              </div>
            </Card>

            <Card className="p-6 sm:p-8 hover-lift cursor-pointer group bg-white border-2 border-transparent hover:border-violet-200">
              <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                <Users className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">
                Team Collaboration
              </h3>
              <p className="text-sm sm:text-base text-gray-600 mb-4">
                Work together seamlessly with your team. Share reports, assign tasks, track progress.
              </p>
              <div className="flex items-center text-green-600 font-medium text-sm group-hover:translate-x-2 transition-transform">
                Learn more <ArrowRight className="w-4 h-4 ml-1" />
              </div>
            </Card>

            <Card className="p-6 sm:p-8 hover-lift cursor-pointer group bg-white border-2 border-transparent hover:border-violet-200">
              <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                <BarChart3 className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">
                Real-Time Analytics
              </h3>
              <p className="text-sm sm:text-base text-gray-600 mb-4">
                Monitor your campaigns with live dashboards. Track every metric that matters.
              </p>
              <div className="flex items-center text-orange-600 font-medium text-sm group-hover:translate-x-2 transition-transform">
                Learn more <ArrowRight className="w-4 h-4 ml-1" />
              </div>
            </Card>

            <Card className="p-6 sm:p-8 hover-lift cursor-pointer group bg-white border-2 border-transparent hover:border-violet-200">
              <div className="w-14 h-14 bg-gradient-to-br from-pink-500 to-pink-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                <Globe className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">
                Global Data Coverage
              </h3>
              <p className="text-sm sm:text-base text-gray-600 mb-4">
                Access data from 190+ countries. Multilingual support for global marketing campaigns.
              </p>
              <div className="flex items-center text-pink-600 font-medium text-sm group-hover:translate-x-2 transition-transform">
                Learn more <ArrowRight className="w-4 h-4 ml-1" />
              </div>
            </Card>

            <Card className="p-6 sm:p-8 hover-lift cursor-pointer group bg-white border-2 border-transparent hover:border-violet-200">
              <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">
                Enterprise Security
              </h3>
              <p className="text-sm sm:text-base text-gray-600 mb-4">
                Enterprise-grade encryption for all data transmission. Your data is always safe and secure with us.
              </p>
              <div className="flex items-center text-cyan-600 font-medium text-sm group-hover:translate-x-2 transition-transform">
                Learn more <ArrowRight className="w-4 h-4 ml-1" />
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* SEO Leaderboard Widget */}
      {topWebsites.length > 0 && (
        <section className="py-12 sm:py-20 bg-gradient-to-br from-violet-50 to-purple-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <Badge className="bg-yellow-100 text-yellow-700 mb-4">
                <Trophy className="w-3 h-3 mr-1" />
                Live Rankings
              </Badge>
              <h2 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-4">
                SEO <span className="text-gradient-animated">Leaderboard</span>
              </h2>
              <p className="text-base sm:text-lg text-gray-600">
                See how top websites score. Submit yours and compete!
              </p>
            </div>
            
            <Card className="overflow-hidden shadow-xl">
              <div className="divide-y">
                {topWebsites.slice(0, 5).map((site, i) => (
                  <div key={site.website_id} className={`p-4 flex items-center gap-4 ${i === 0 ? 'bg-gradient-to-r from-yellow-50 to-transparent' : ''}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                      i === 0 ? 'bg-yellow-400 text-yellow-900' :
                      i === 1 ? 'bg-gray-300 text-gray-700' :
                      i === 2 ? 'bg-orange-300 text-orange-800' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {i + 1}
                    </div>
                    <div className="flex-1">
                      <span className="font-semibold">{site.domain}</span>
                      <span className="text-gray-500 text-sm ml-2">by {site.owner_name}</span>
                    </div>
                    <div className={`px-3 py-1 rounded-lg font-bold ${
                      site.seo_score >= 80 ? 'text-green-600 bg-green-50' :
                      site.seo_score >= 60 ? 'text-yellow-600 bg-yellow-50' :
                      'text-orange-600 bg-orange-50'
                    }`}>
                      {site.seo_score}
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-4 bg-gray-50 flex flex-col sm:flex-row items-center justify-between gap-4">
                <p className="text-gray-600 text-sm">Think you can beat them?</p>
                <Button onClick={() => navigate('/leaderboard')} className="bg-gradient-to-r from-violet-600 to-purple-600">
                  <Trophy className="w-4 h-4 mr-2" /> Submit Your Website
                </Button>
              </div>
            </Card>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <Badge className="bg-violet-100 text-violet-700 mb-4">
              <HelpCircle className="w-3 h-3 mr-1" />
              FAQs
            </Badge>
            <h2 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-base sm:text-lg text-gray-600">
              Everything you need to know about MarkeeMix
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="border rounded-xl px-6 bg-gray-50 hover:bg-gray-100 transition-colors">
              <AccordionTrigger className="text-left font-semibold text-gray-900 hover:no-underline py-5">
                MarkeeMix क्या है और यह कैसे काम करता है?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 pb-5">
                MarkeeMix एक AI workspace for teachers, creators & marketers है जिसमें 75+ tools हैं। इसमें SEO analysis, keyword research, content writing, PDF editing, social media management, और advertising tools शामिल हैं। आप simply sign up करें, tool select करें, और अपना काम शुरू करें - कोई technical knowledge की जरूरत नहीं!
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="border rounded-xl px-6 bg-gray-50 hover:bg-gray-100 transition-colors">
              <AccordionTrigger className="text-left font-semibold text-gray-900 hover:no-underline py-5">
                क्या MarkeeMix free है?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 pb-5">
                हाँ! MarkeeMix का Free plan हमेशा के लिए free है जिसमें आपको daily 5 keyword searches, 3 AI content generations, basic SEO audit, और कई PDF tools मिलते हैं। Pro plan (₹499/month) में unlimited access और advanced features मिलते हैं।
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="border rounded-xl px-6 bg-gray-50 hover:bg-gray-100 transition-colors">
              <AccordionTrigger className="text-left font-semibold text-gray-900 hover:no-underline py-5">
                SEO tools से मेरी website की ranking कैसे improve होगी?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 pb-5">
                हमारे SEO tools आपको बताते हैं कि आपकी website में क्या कमी है। Site Audit से technical issues पता चलते हैं, Keyword Research से best keywords मिलते हैं, Backlink Analyzer से link building में help मिलती है, और Rank Tracking से आप daily progress देख सकते हैं। इन सबको follow करके ranking improve होती है।
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="border rounded-xl px-6 bg-gray-50 hover:bg-gray-100 transition-colors">
              <AccordionTrigger className="text-left font-semibold text-gray-900 hover:no-underline py-5">
                PDF tools में क्या-क्या features हैं?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 pb-5">
                हमारे पास 40+ PDF tools हैं: Merge (जोड़ना), Split (अलग करना), Compress (size कम करना), Watermark add करना, Password protect करना, PDF to Image, Image to PDF, Text extract करना, OCR (scanned PDF से text निकालना), Header/Footer add करना, और बहुत कुछ। सब FREE में!
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="border rounded-xl px-6 bg-gray-50 hover:bg-gray-100 transition-colors">
              <AccordionTrigger className="text-left font-semibold text-gray-900 hover:no-underline py-5">
                क्या मेरा data safe है?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 pb-5">
                बिल्कुल! हम enterprise-grade encryption use करते हैं। आपकी uploaded files 24 hours में automatically delete हो जाती हैं। हम कभी भी आपका data third-party के साथ share नहीं करते। Google OAuth से secure login मिलता है।
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-6" className="border rounded-xl px-6 bg-gray-50 hover:bg-gray-100 transition-colors">
              <AccordionTrigger className="text-left font-semibold text-gray-900 hover:no-underline py-5">
                Content Writer tool कैसे काम करता है?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 pb-5">
                हमारा AI Content Writer GPT-powered है। आप बस topic दें, और यह SEO-optimized articles, blog posts, product descriptions, social media posts generate कर देता है। आप tone (professional/casual), length, और language select कर सकते हैं। Plagiarism-free content guarantee!
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-7" className="border rounded-xl px-6 bg-gray-50 hover:bg-gray-100 transition-colors">
              <AccordionTrigger className="text-left font-semibold text-gray-900 hover:no-underline py-5">
                क्या team members को add कर सकते हैं?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 pb-5">
                हाँ! Pro और Enterprise plans में team collaboration available है। आप team members invite कर सकते हैं, roles assign कर सकते हैं (Admin, Editor, Viewer), projects share कर सकते हैं, और Task Manager से सबका काम track कर सकते हैं।
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-8" className="border rounded-xl px-6 bg-gray-50 hover:bg-gray-100 transition-colors">
              <AccordionTrigger className="text-left font-semibold text-gray-900 hover:no-underline py-5">
                Payment methods क्या हैं? Refund policy क्या है?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 pb-5">
                हम Razorpay accept करते हैं - Credit/Debit Cards, UPI, Net Banking, Wallets सब चलते हैं। अगर आप satisfied नहीं हैं तो first 7 days में full refund मिलता है, no questions asked। Monthly plans कभी भी cancel कर सकते हैं।
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <div className="text-center mt-10">
            <p className="text-gray-600 mb-4">Still have questions?</p>
            <Button 
              variant="outline" 
              className="hover-lift"
              onClick={() => navigate('/contact')}
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Contact Support
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section - Enhanced with Animation */}
      <section className="py-12 sm:py-20 bg-gradient-to-br from-violet-700 via-purple-600 to-indigo-700 relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/10 rounded-full filter blur-3xl animate-float" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white/10 rounded-full filter blur-3xl animate-float-delay-2" />
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-medium mb-6">
            <Rocket className="w-4 h-4" />
            Start Free Today - No Credit Card Required
          </div>
          
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6">
            Join 50,000+ Marketers <span className="text-shimmer">Worldwide</span>
          </h2>
          <p className="text-base sm:text-xl text-violet-100 mb-8 sm:mb-10 max-w-2xl mx-auto">
            Stop juggling multiple tools. Get everything you need to grow your business in one powerful platform.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="h-14 px-10 bg-white text-violet-700 hover:bg-gray-50 text-lg shadow-xl hover-lift group"
              onClick={() => navigate("/tools")}
            >
              Start Your Free Trial
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="h-14 px-10 text-lg border-white/30 text-white hover:bg-white/10"
              onClick={() => navigate("/pricing")}
            >
              View Pricing
            </Button>
          </div>
          
          <div className="flex flex-wrap justify-center gap-6 mt-10">
            <div className="flex items-center gap-2 text-white/80">
              <CheckCircle2 className="w-5 h-5 text-green-300" />
              <span>No credit card required</span>
            </div>
            <div className="flex items-center gap-2 text-white/80">
              <Clock className="w-5 h-5 text-blue-300" />
              <span>7-day free trial</span>
            </div>
            <div className="flex items-center gap-2 text-white/80">
              <Star className="w-5 h-5 text-yellow-300" />
              <span>Cancel anytime</span>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="bg-gray-800 py-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-r from-violet-900/50 to-purple-900/50 rounded-xl p-8 border border-violet-700/50">
            <h3 className="text-2xl font-bold text-white mb-3">Subscribe to Our Newsletter</h3>
            <p className="text-gray-400 mb-6 max-w-lg mx-auto">Get the latest marketing tips, SEO insights & tool updates delivered to your inbox.</p>
            
            {newsletterSubscribed ? (
              <div className="flex items-center justify-center gap-2 text-green-400 py-4">
                <CheckCircle className="w-6 h-6" />
                <span className="text-lg">Thanks for subscribing! Check your email.</span>
              </div>
            ) : (
              <form onSubmit={handleNewsletterSubscribe} className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  className="bg-gray-800/50 border-gray-600 text-white placeholder:text-gray-500 h-12"
                  data-testid="newsletter-email-input"
                />
                <Button 
                  type="submit" 
                  disabled={newsletterLoading}
                  className="bg-violet-600 hover:bg-violet-700 text-white h-12 px-8"
                  data-testid="newsletter-subscribe-btn"
                >
                  {newsletterLoading ? (
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Subscribe
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-8">
            <div>
              <h4 className="text-white font-semibold mb-4">SEO Tools</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/tools/seo/dashboard" className="hover:text-white">SEO Dashboard</a></li>
                <li><a href="/tools/seo" className="hover:text-white">Keyword Research</a></li>
                <li><a href="/tools/seo" className="hover:text-white">Backlink Analyzer</a></li>
                <li><a href="/tools/seo/technical" className="hover:text-white">Technical SEO</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Content</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/tools/content" className="hover:text-white">AI Content Writer</a></li>
                <li><a href="/tools/content" className="hover:text-white">Plagiarism Checker</a></li>
                <li><a href="/blog" className="hover:text-white">Blog Manager</a></li>
                <li><a href="/blogs" className="hover:text-white">Read Blogs</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Design</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/tools/design" className="hover:text-white">Logo Maker</a></li>
                <li><a href="/tools/design" className="hover:text-white">Banner Creator</a></li>
                <li><a href="/tools/design/pdf" className="hover:text-white">PDF Tools (32+)</a></li>
                <li><a href="/tools/social-media" className="hover:text-white">Social Media</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/about" className="hover:text-white">About Us</a></li>
                <li><a href="/contact" className="hover:text-white">Contact</a></li>
                <li><a href="/pricing" className="hover:text-white">Pricing</a></li>
                <li><a href="/blogs" className="hover:text-white">Blog</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/privacy" className="hover:text-white">Privacy Policy</a></li>
                <li><a href="/terms" className="hover:text-white">Terms of Service</a></li>
                <li><a href="/refund" className="hover:text-white">Refund Policy</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Contact Us</h4>
              <ul className="space-y-3 text-sm">
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-violet-400" />
                  <a href="tel:+918269911081" className="hover:text-white">+91 8269911081</a>
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-violet-400" />
                  <a href="mailto:contact@markeemix.com" className="hover:text-white">contact@markeemix.com</a>
                </li>
                <li className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-violet-400 mt-0.5" />
                  <span>Indore, MP, India</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <span className="text-xl font-bold bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent">
                MarkeeMix
              </span>
            </div>
            <p className="text-sm">
              © 2026 CodewithKamlesh. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
      
      {/* WhatsApp Chat Button */}
      <WhatsAppChat phoneNumber="918269911081" />
    </div>
  );
}